/**
 * GameLobby orchestration hook.
 *
 * Lifts ALL of the lobby's data orchestration and derived view-model state out
 * of the component render body so the GameLobby shell stays a thin composition.
 * Every server read here is a react-query hook (no useState + useEffect + fetch),
 * so the reads run as parallel cached queries that dedupe across surfaces -
 * directly addressing the audit's "request waterfall" and "fetched twice per
 * page" findings.
 *
 * The component consumes the returned view-model and passes slices to the
 * module-scope section components.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../../../contexts/AuthContext";
import { useKYC } from "../../../shared/hooks/useKYC";
import { useFavorites } from "../../../shared/hooks/useFavorites";
import useFirstPurchaseOffer from "../../../shared/hooks/useFirstPurchaseOffer";
import { useGameCounts } from "../../../hooks/useGameCounts";
import useCountUp from "../../../shared/hooks/useCountUp";

import promotions from "../../promotions/promotionsData";
import { buildPromoCards } from "../../../shared/utils/promoCardBuilder";
import { navigateToCBGame } from "../../../services/gameNavigationService";
import { queryKeys } from "../../../shared/data/queryKeys";
import { trackEvent } from "../../../shared/services/playerEvents";

import { useGameCatalog } from "./useGameCatalog";
import {
  useEnabledGamesCount,
  useEnabledProvidersCount,
} from "./useLobbyCounts";
import { useLobbyProviders } from "./useLobbyProviders";
import { useRecentlyPlayed } from "./useRecentlyPlayed";
import { useFavoriteGames } from "./useFavoriteGames";
import { useRecommendations } from "./useRecommendations";
import { usePurchaseStatus } from "./usePurchaseStatus";
import { useSpecialOffersAvailable } from "./useSpecialOffersAvailable";

const MS_30_DAYS = 30 * 24 * 60 * 60 * 1000;

export function useGameLobby() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLoggedIn, user } = useAuth();
  const userId = user?.id;

  const { counts: gameCounts } = useGameCounts();
  const { isVerified, loading: kycLoading } = useKYC();
  const { offer: firstOffer, loading: firstOfferLoading } =
    useFirstPurchaseOffer();

  // ── Mobile breakpoint ──────────────────────────────────────────────────────
  // Matches Tailwind's `md` (768px) so the JS fork stays in lockstep with any
  // `md:`/`hidden md:block` classes. Initialised lazily from matchMedia so the
  // very first paint is already correct - no desktop-then-mobile flip (CLS) on
  // load. Listens via matchMedia change rather than a width resize listener.
  const MOBILE_QUERY = "(max-width: 767px)";
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(MOBILE_QUERY).matches
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function")
      return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    // Sync once in case the viewport changed between initial render and effect.
    setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // ── Server reads (parallel cached queries) ─────────────────────────────────
  const catalogQuery = useGameCatalog();
  const enabledGamesQuery = useEnabledGamesCount();
  const enabledProvidersQuery = useEnabledProvidersCount();
  const providersQuery = useLobbyProviders();
  const recentlyPlayedQuery = useRecentlyPlayed(userId);
  const favoriteGamesQuery = useFavoriteGames(userId);
  const specialOffersQuery = useSpecialOffersAvailable(isLoggedIn);
  const purchaseStatusQuery = usePurchaseStatus(userId);

  // Recommendations are fetched (mirrors prior behaviour - they refresh after a
  // launch and feed downstream surfaces) even though the lobby itself does not
  // render a recommendations row.
  useRecommendations(isLoggedIn, userId);

  const gameCatalog = catalogQuery.data;
  const enabledGamesCount = enabledGamesQuery.data ?? null;
  const enabledProvidersCount = enabledProvidersQuery.data ?? null;
  const displayCount = useCountUp(enabledGamesCount);

  // ── Favorites (heart toggle across the whole lobby) ────────────────────────
  const { toggleFavorite: originalToggleFavorite, isFavorited } =
    useFavorites();

  // Wrap toggle so the dedicated Favorites row refetches after a change.
  const toggleFavorite = useCallback(
    async (gameCode) => {
      const result = await originalToggleFavorite(gameCode);
      queryClient.invalidateQueries({
        queryKey: queryKeys.games.favorites(userId),
      });
      return result;
    },
    [originalToggleFavorite, queryClient, userId],
  );

  // ── Game launch ────────────────────────────────────────────────────────────
  const handleGameLaunch = useCallback(
    (gameCode) => {
      // player_events funnel: a game tile was clicked/tapped in the lobby.
      // Fire-and-forget; never blocks the navigation below.
      trackEvent("game_tile_click", { game_code: gameCode });

      navigateToCBGame(navigate, gameCode);
      // Recently-played + recommendations change as a result of the launch.
      // Invalidate (don't imperatively refetch) so react-query refreshes the
      // shared cache entries on the player's next visit / when remounted.
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.games.recentlyPlayed(userId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.games.recommendations(userId),
        });
      }, 2000);
    },
    [navigate, queryClient, userId],
  );

  // ── Promo list / cards ─────────────────────────────────────────────────────
  // Raw filtered list (welcome cards hidden once verified). This is the input to
  // buildPromoCards for the steady state.
  const promoList = useMemo(() => {
    let list = promotions.slice();
    if (isVerified) list = list.filter((p) => p.variant !== "welcome");
    return list;
  }, [isVerified]);

  // The huge_collection card is rendered directly (not via buildPromoCards)
  // during the brief promosLoading window. Resolve its dynamic count copy here so
  // the presentational LobbyHero / PromotionsSection components stay dumb. Every
  // other entry passes through unchanged.
  const promoListReady = useMemo(
    () =>
      promoList.map((p) => {
        if (p.id !== "huge_collection") return p;

        const resolvedCount =
          typeof enabledGamesCount === "number"
            ? enabledGamesCount
            : typeof displayCount === "number"
              ? displayCount
              : 0;
        const gamesText =
          resolvedCount >= 1000
            ? "1,000+"
            : Number.isFinite(resolvedCount)
              ? resolvedCount.toLocaleString()
              : "0";
        const providersText =
          typeof enabledProvidersCount === "number"
            ? enabledProvidersCount
            : 0;

        return {
          ...p,
          title:
            typeof enabledGamesCount === "number"
              ? `${enabledGamesCount.toLocaleString()} Games`
              : `${gamesText} Games`,
          subtitle: p.subtitle ?? p.title,
          description: `${gamesText} games across ${providersText} providers. New games added daily.`,
        };
      }),
    [promoList, enabledGamesCount, displayCount, enabledProvidersCount],
  );

  const promoCards = useMemo(() => {
    const ctx = {
      isVerified,
      firstTimeOffers: firstOffer ? [firstOffer] : [],
      firstOffer,
      navigate,
      enabledGamesCount,
      displayCount,
      enabledProvidersCount,
    };
    return promoList.flatMap((p) => buildPromoCards(p, ctx));
  }, [
    promoList,
    isVerified,
    firstOffer,
    navigate,
    enabledGamesCount,
    displayCount,
    enabledProvidersCount,
  ]);

  const promosLoading = kycLoading || firstOfferLoading;

  // ── Section kickers (derived, no extra requests) ───────────────────────────
  const newSlotsLast30Days = useMemo(() => {
    const slots = gameCatalog?.slots;
    if (!slots?.length) return 0;
    const cutoff = Date.now() - MS_30_DAYS;
    return slots.filter((g) => {
      const t = new Date(
        g.release_date || g.created_at || g.createdAt || 0,
      ).getTime();
      return Number.isFinite(t) && t >= cutoff;
    }).length;
  }, [gameCatalog?.slots]);

  const slotsKicker = useMemo(() => {
    if (newSlotsLast30Days > 0)
      return `${newSlotsLast30Days} new · last 30 days`;
    if (typeof gameCounts.slots === "number" && gameCounts.slots > 0) {
      return `${gameCounts.slots.toLocaleString()} slots`;
    }
    return "Just added";
  }, [newSlotsLast30Days, gameCounts.slots]);

  const providersKicker = useMemo(() => {
    const games =
      typeof enabledGamesCount === "number" && enabledGamesCount > 0
        ? enabledGamesCount
        : null;
    const provs =
      typeof enabledProvidersCount === "number" && enabledProvidersCount > 0
        ? enabledProvidersCount
        : null;
    if (!games || !provs) return null;
    return `${games.toLocaleString()} games across ${provs} providers`;
  }, [enabledGamesCount, enabledProvidersCount]);

  const tableKicker = useMemo(() => {
    if (
      typeof gameCounts.table_games === "number" &&
      gameCounts.table_games > 0
    ) {
      return `${gameCounts.table_games.toLocaleString()} table games · the classics`;
    }
    return "The classics";
  }, [gameCounts.table_games]);

  const instantKicker = useMemo(() => {
    if (typeof gameCounts.instant === "number" && gameCounts.instant > 0) {
      return `${gameCounts.instant.toLocaleString()} games · crash · scratch · instant win`;
    }
    return "Crash · scratch · instant win";
  }, [gameCounts.instant]);

  return {
    // chrome / context
    navigate,
    isMobile,
    isLoggedIn,
    isVerified,

    // promo
    promoList: promoListReady,
    promoCards,
    promosLoading,
    enabledGamesCount,
    displayCount,
    enabledProvidersCount,
    firstOffer,

    // catalog
    catalogQuery,
    gameCatalog,

    // providers
    providersQuery,

    // personal rows
    recentlyPlayedQuery,
    favoriteGamesQuery,

    // offers / modal
    specialOffersAvailable: Boolean(specialOffersQuery.data),
    purchaseStatus: purchaseStatusQuery.data,

    // kickers
    slotsKicker,
    providersKicker,
    tableKicker,
    instantKicker,

    // actions
    toggleFavorite,
    isFavorited,
    handleGameLaunch,
  };
}

export default useGameLobby;
